/* 3.10 — quick-add bottom sheet, opened from the card's `+`.

   Replaces Madrid's quick-add-modal, which fetched the entire product page on every
   tap (~1000ms) and injected the whole main-product section so CSS could hide most
   of it. The panel is now pre-rendered into each card by snippets/stl-quick-sheet.liquid,
   so opening it costs no network at all — the approach both reference sites use
   (camillaandmarc.com, assemblylabel.com).

   Adding to cart mirrors assets/product-form.js exactly, the same way
   stl-card-size-add.js does: same fetchConfig, the same `sections` / `sections_url`
   payload the drawer needs to re-render, and cart.renderContents(response). One
   delegated listener rather than a handler per card. */

(function () {
  'use strict';

  var OPEN = 'stl-quick-sheet--open';
  var openSheet = null;

  function cart() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  function close() {
    if (!openSheet) return;
    openSheet.classList.remove(OPEN);
    openSheet.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('stl-quick-sheet-lock');
    openSheet = null;
  }

  function open(sheet) {
    if (openSheet && openSheet !== sheet) close();
    /* One class, nothing else. The sheet is never removed from layout — it sits at
       `visibility: hidden; pointer-events: none` — so there is no `hidden` attribute
       to remove, no forced reflow to make the transition run, and no
       requestAnimationFrame to be suspended in a background tab.

       Both of those were tried. rAF stranded the sheet half-open whenever the tab
       was not focused: `hidden` already gone but the class never applied, leaving a
       transparent full-viewport layer that swallowed every tap on the grid. The
       forced reflow that replaced it worked but cost ~137ms on a 21-card page,
       because reading offsetHeight invalidates the whole document. */
    /* MOVE THE SHEET TO <body> BEFORE SHOWING IT.

       The sheet is `position: fixed`, which normally makes its containing block the
       viewport — but a `transform` on any ancestor takes that over, and Swiper puts
       `transform: translate3d(...)` on `.swiper-wrapper`. So on every carousel
       surface (Complete the Look, You May Also Like, Recently Viewed, the homepage
       sliders) the sheet was trapped inside the slide: it opened with the right
       product and the right sizes, but measured 362 wide at x=20 and floated 174px
       above the bottom of the screen instead of sitting flush and full width.

       The PLP never showed this because its grid is a plain `<ul>` with no
       transform anywhere in the chain.

       Re-parenting is enough — the sheet carries its own content and its handlers
       are delegated on `document`, so nothing is lost by the move, and it also
       settles the stacking question for good: at <body> level no ancestor can trap
       the z-index either. Done once per sheet; a sheet already moved stays moved. */
    if (sheet.parentNode !== document.body) document.body.appendChild(sheet);

    sheet.setAttribute('aria-hidden', 'false');
    sheet.classList.add(OPEN);
    document.documentElement.classList.add('stl-quick-sheet-lock');
    openSheet = sheet;
  }

  /* Selecting a size updates the label row and arms the button. Both states are
     already on the input from Liquid, so nothing is computed here. */
  function select(input) {
    var sheet = input.closest('[data-stl-quick-sheet]');
    if (!sheet) return;

    var name = sheet.querySelector('[data-stl-size-name]');
    var state = sheet.querySelector('[data-stl-size-state]');
    var add = sheet.querySelector('[data-stl-quick-add]');

    if (name) name.textContent = name.textContent.split(':')[0].trim() + ' : ' + input.value;
    if (state) state.textContent = input.getAttribute('data-stl-state') || '';

    if (add) {
      var soldOut = input.classList.contains('disabled');
      /* Sold out stays clickable — it is the Notify Me trigger (G6). */
      add.disabled = false;
      add.toggleAttribute('data-stl-notify', soldOut);
      add.setAttribute('data-stl-variant', input.getAttribute('data-stl-variant') || '');
      add.textContent = soldOut ? 'Notify Me When Available' : 'Add to Cart';
    }
  }

  /* FIND THE SHEET IN A WAY THAT SURVIVES THE MOVE.

     `open()` re-parents the sheet to <body> and leaves it there, so after the first
     open it is no longer inside the card and a `card.querySelector` lookup returns
     null. That is exactly one open per product per page load: the `+` did nothing
     from the second tap onwards, with no error (Serena, 18 Sep 2026, on mobile).

     The reference is cached on the OPENER the first time it is resolved. A grid
     re-render (a filter or a sort) replaces both elements, so the new opener has no
     cache and finds the new in-card sheet — the cache can never go stale without its
     opener going with it. `isConnected` guards the one case it could: a sheet removed
     from the document while its opener survives. */
  function sheetFor(opener) {
    if (opener.stlSheet && opener.stlSheet.isConnected) return opener.stlSheet;
    var card = opener.closest('.card-wrapper') || opener.closest('li');
    var sheet = card && card.querySelector('[data-stl-quick-sheet]');
    if (sheet) opener.stlSheet = sheet;
    return sheet;
  }

  document.addEventListener('click', function (event) {
    var opener = event.target.closest('[data-stl-quick-open]');
    if (opener) {
      var sheet = sheetFor(opener);
      if (sheet) {
        event.preventDefault();
        open(sheet);
      }
      return;
    }

    if (event.target.closest('[data-stl-quick-close]')) { close(); return; }

    /* Tapping the scrim closes. The panel stops the bubble itself. */
    var sheetHit = event.target.closest('[data-stl-quick-sheet]');
    if (sheetHit && !event.target.closest('.stl-quick-sheet__panel')) { close(); return; }

    var add = event.target.closest('[data-stl-quick-add]');
    if (add) { addToCart(add); }
  });

  document.addEventListener('change', function (event) {
    var input = event.target.closest('[data-stl-quick-sheet] input[type="radio"]');
    if (input) select(input);
  });

  document.addEventListener('keyup', function (event) {
    if (event.key === 'Escape') close();
  });

  function addToCart(button) {
    var variantId = button.getAttribute('data-stl-variant');

    /* Notify Me: close the sheet first — it sits above everything, Amp's form
       included — then hand the variant to Amp. Without Amp on the page, go to the
       product page on that size, where Madrid's own notify modal is available. */
    if (button.hasAttribute('data-stl-notify')) {
      var handle = button.getAttribute('data-stl-product-handle');
      close();
      if (!(typeof window.stlOpenBackInStock === 'function' && window.stlOpenBackInStock(handle, variantId))) {
        var productUrl = button.getAttribute('data-stl-product-url');
        if (productUrl) window.location = productUrl + '?variant=' + variantId;
      }
      return;
    }

    if (!variantId || button.disabled || button.hasAttribute('aria-busy')) return;
    button.setAttribute('aria-busy', 'true');

    var c = cart();

    if (typeof fetchConfig !== 'function' || typeof routes === 'undefined') {
      var url = button.getAttribute('data-stl-product-url');
      if (url) window.location = url + '?variant=' + variantId;
      button.removeAttribute('aria-busy');
      return;
    }

    var config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    var body = new FormData();
    body.append('id', variantId);
    body.append('quantity', 1);

    if (c) {
      body.append('sections', c.getSectionsToRender().map(function (s) { return s.id; }));
      body.append('sections_url', window.location.pathname);
      c.setActiveElement(document.activeElement);
    }

    config.body = body;

    fetch(routes.cart_add_url, config)
      .then(function (r) { return r.json(); })
      .then(function (response) {
        if (response.status) {
          document.dispatchEvent(new CustomEvent('cart:error', {
            detail: { source: 'stl-quick-sheet', productVariantId: variantId, errors: response.description, message: response.message }
          }));
          return;
        }
        close();
        if (c) c.renderContents(response);
      })
      .catch(function (e) { console.error(e); })
      .finally(function () { button.removeAttribute('aria-busy'); });
  }
})();
